/* eslint-disable react/no-danger */
import React from 'react';
import styled from 'styled-components';
import { Link, graphql } from 'gatsby';
import DOMPurify from 'isomorphic-dompurify';
import { useWindowSize } from 'hooks';
import {
  TextAndImageBlock,
  SEO,
  StaircaseCard,
  MultiColumnCarousel,
  Button,
  GoogleReviews
  // Testimonials
} from 'components';
import { Hero } from 'components/ProductPage/Shared';
import { getAltTagFromFileName, slugify } from 'utils';

function TradePartners({ data }) {
  const { windowWidth } = useWindowSize();
  const { heroData, carpenterConstructingDoor, stairsWithBlackRailings, showcases } = data;
  const { heroImage, heroTitle, heroDescription } = heroData.frontmatter;

  const _showcases = [...showcases.nodes].map((node) => {
    const { id, title, images } = node;
    const image = images[0].asset;
    const slug = slugify(title);
    return (
      <StaircaseCard
        key={id}
        id={id}
        slug={`/showcases/${slug}`}
        image={image.gatsbyImageData}
        title={title}
        alt={getAltTagFromFileName(image.originalFilename)}
      />
    );
  });

  return (
    <>
      <Hero
        height={windowWidth < 650 ? '286px' : '15.75rem'}
        heroImage={heroImage}
        heroImageOffset={-100}
        filter="contrast(115%) saturate(160%)"
        gradientPercentage="80%"
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Carpenter smoothing plank of wood"
        breadcrumb="Trade Partners">
        <h1
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroTitle) }}
          style={{ fontSize: '2rem' }}
        />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroDescription) }} />
      </Hero>
      <Grid $backgroundColor={windowWidth < 1200 ? 'var(--connolly-grey)' : '#fff'}>
        <TextAndImageBlock
          image={carpenterConstructingDoor.childImageSharp.gatsbyImageData}
          imageTitle="About Us"
          imageAlt={getAltTagFromFileName(carpenterConstructingDoor.name)}
          paddingTop={windowWidth < 1200 ? '49px' : '188px'}>
          <h3>Expert Staircase Joinery</h3>
          <p>
            As a recognised staircase joinery, we are proud to supply Ireland&apos;s premier
            builders and merchants. Over time, we have cultivated strong partnerships with our
            clients, building our reputation on trust and craftsmanship for developments of all
            sizes, from one off builds to larger scale projects.
            <br />
            <br />
            Today, we are proud to serve many of Ireland&apos;s top builders along with some of the
            nation&apos;s most respected specialists and bespoke developers. They rely on us for
            high-quality products, delivered efficiently and within tight deadlines.
          </p>
          <Button to="/stairs/" style={{ width: '180px' }}>
            View Staircases
          </Button>
        </TextAndImageBlock>
        <TextAndImageBlock
          image={stairsWithBlackRailings.childImageSharp.gatsbyImageData}
          imageTitle="Oak Stairs"
          imageAlt={getAltTagFromFileName(stairsWithBlackRailings.name)}
          flip
          paddingTop={windowWidth < 1200 ? '39px' : '0px'}>
          <h3 style={{ marginBottom: '50px' }}>
            We&apos;re ready to
            <br />
            discuss your project
          </h3>
          <Button
            to="/contact-us/"
            style={{ width: '180px', marginRight: '20px', whiteSpace: 'nowrap' }}>
            Contact Us Today
          </Button>
        </TextAndImageBlock>
      </Grid>
      <StyledMultiColumnCarousel
        title="Past Projects Gallery"
        cards={_showcases}
        backgroundColor="#fff"
        responsive={{
          desktop: {
            breakpoint: { max: 10000, min: 1335 },
            items: 3
          },
          tablet: {
            breakpoint: { max: 1334, min: 995 },
            items: 2
          },
          mobile: {
            breakpoint: { max: 994, min: 0 },
            items: 1
          }
        }}
      />
      <GoogleReviews className="fade-in" backgroundColor="#fff" />
      {/* <Testimonials title="Read What Our Customers Say About Us" backgroundColor="#fff" /> */}
    </>
  );
}

const Grid = styled.section`
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'var(--connolly-grey)'};
  column-gap: 20px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 100px));
  justify-content: center;
  padding: 0 1.25rem;

  @media (width >= 768px) {
    padding: 0 0.625rem;
  }
`;

const StyledMultiColumnCarousel = styled(MultiColumnCarousel)`
  padding-bottom: 112px;
  width: 100%;

  > h3 {
    padding: 1.25em 0 0.85em;

    @media (width >= 768px) {
      padding: 1.906em 0 1.1em;
    }
  }

  > div {
    /* Width of Arrow Buttons */
    max-width: 360px;

    /* Width of Slides container */
    > div {
      max-width: 360px;
    }
  }

  @media (width >= 380px) {
    > div {
      max-width: 440px;

      > div {
        max-width: 380px;
      }
    }
  }

  @media (width >= 914px) {
    padding-bottom: 39px;

    > div {
      max-width: 820px;

      > div {
        max-width: 760px;
      }
    }
  }

  @media (width >= 1300px) {
    > div {
      max-width: 1200px;

      > div {
        max-width: 1140px;
      }
    }
  }

  .react-multi-carousel-dot button {
    border: none;
    width: 0.625rem;
    height: 0.625rem;
  }

  .react-multi-carousel-dot--active button {
    background-color: var(--connolly-red);
  }

  .react-multi-carousel-item a {
    height: 23.75rem;
    max-width: 22.5rem;
    width: 100%;

    .gatsby-image-wrapper {
      height: 23.75rem;
    }
  }
`;

export const query = graphql`
  query {
    heroData: markdownRemark(frontmatter: { title: { eq: "Trade Partners" } }) {
      id
      frontmatter {
        heroTitle
        heroDescription
        heroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
    }
    carpenterConstructingDoor: file(
      relativePath: { eq: "trade-partners/carpenter-constructing-door.png" }
    ) {
      name
      childImageSharp {
        gatsbyImageData(width: 590, height: 590, formats: [AUTO, WEBP, AVIF])
      }
    }
    stairsWithBlackRailings: file(
      relativePath: {
        eq: "trade-partners/closed-riser-closed-string-stairs-with-black-railings.png"
      }
    ) {
      name
      childImageSharp {
        gatsbyImageData(width: 590, height: 300, formats: [AUTO, WEBP, AVIF])
      }
    }
    showcases: allSanityShowcase {
      nodes {
        id
        title
        images {
          _key
          hotspot {
            height
            width
            x
            y
          }
          crop {
            top
            bottom
            left
            right
          }
          asset {
            _id
            originalFilename
            url
            metadata {
              dimensions {
                width
                height
              }
            }
          }
        }
      }
    }
  }
`;

export function Head() {
  return (
    <SEO
      title="Trade Partners | Connolly Stairs & Doors"
      description="Partner with Connolly Stairs & Doors for reliable supply and superior quality stairs and doors. Supporting trade professionals across Ireland.">
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Stairs Ireland", "item": "https://www.connollystairs.ie/" },
          { "@type": "ListItem", "position": 2, "name": "Trade Partners", "item": "https://www.connollystairs.ie/trade-partners/" }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default TradePartners;
